import React, {useCallback, useRef, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {useValidateCertificate} from '../../../hooks/use-validate-certificate';
import {errorText, fieldGroup, loadingIndicator, successIndicator, tabContent, warningText} from '../upload-metadata.module.styl';
import {validateLoginUrl, validatePemCertificate} from '../utils/metadata-validation';

import {ProviderInstructions} from './provider-instructions';

export type ManualTabData = {
    loginUrl: string,
    certificate: string,
    valid: boolean,
};

type ManualEntryTabProps = {
    data: ManualTabData,
    onChange: (data: ManualTabData) => void,
    providerId: string | null,
};

export const ManualEntryTab: React.FC<ManualEntryTabProps> = ({data, onChange, providerId}) => {
    const [urlError, setUrlError] = useState<string | null>(null);
    const [urlWarning, setUrlWarning] = useState<string | null>(null);
    const [certError, setCertError] = useState<string | null>(null);
    const {validate, loading: certLoading, error: apiError, clearError: clearApiError} = useValidateCertificate();
    const urlPassedRef = useRef(false);
    const certPassedRef = useRef(false);
    const {metadataValidate} = useEventTracking();

    const handleUrlChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        urlPassedRef.current = false;
        clearApiError();
        onChange({...data, loginUrl: event.target.value, valid: false});
        setUrlError(null);
        setUrlWarning(null);
    }, [data, onChange, clearApiError]);

    const handleUrlBlur = useCallback(async () => {
        if (data.loginUrl.trim().length === 0) {
            urlPassedRef.current = false;
            setUrlError(null);
            onChange({...data, valid: false});
            return;
        }
        const result = validateLoginUrl(data.loginUrl);
        setUrlError(result.error);
        setUrlWarning(result.warning ?? null);
        urlPassedRef.current = result.valid;
        if (!result.valid) {
            metadataValidate({method: 'manual', valid: false});
        }

        if (urlPassedRef.current && certPassedRef.current) {
            const ok = await validate(data.certificate);
            metadataValidate({method: 'manual', valid: ok});
            onChange({...data, valid: ok});
        }
    }, [data, onChange, validate, metadataValidate]);

    const handleCertChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        certPassedRef.current = false;
        clearApiError();
        onChange({...data, certificate: event.target.value, valid: false});
        setCertError(null);
    }, [data, onChange, clearApiError]);

    const handleCertBlur = useCallback(async () => {
        if (data.certificate.trim().length === 0) {
            certPassedRef.current = false;
            setCertError(null);
            onChange({...data, valid: false});
            return;
        }
        const result = validatePemCertificate(data.certificate);
        setCertError(result.error);
        certPassedRef.current = result.valid;
        if (!result.valid) {
            metadataValidate({method: 'manual', valid: false});
        }

        if (urlPassedRef.current && certPassedRef.current) {
            const ok = await validate(data.certificate);
            metadataValidate({method: 'manual', valid: ok});
            onChange({...data, valid: ok});
        }
    }, [data, onChange, validate, metadataValidate]);

    const handleDismiss = useCallback(() => {
        urlPassedRef.current = false;
        certPassedRef.current = false;
        setUrlError(null);
        setUrlWarning(null);
        setCertError(null);
        clearApiError();
        onChange({loginUrl: '', certificate: '', valid: false});
    }, [onChange, clearApiError]);

    if (data.valid) {
        return (
            <div className={tabContent}>
                <ProviderInstructions providerId={providerId} tabKey="manual"/>
                <Group gap="xs" alignItems="center" className={successIndicator}>
                    <Icon type={Icons.CHECK} size="Size_14"/>
                    <span>IdP Login URL and signing certificate retrieved successfully</span>
                    <FlatButton
                        style={{marginLeft: 'auto'}}
                        onClick={handleDismiss}>
                        Clear IdP Metadata
                    </FlatButton>
                </Group>
            </div>
        );
    }

    return (
        <div className={tabContent}>
            <ProviderInstructions providerId={providerId} tabKey="manual"/>

            <div className={fieldGroup}>
                <Label>IdP Login URL</Label>
                <Input
                    value={data.loginUrl}
                    onChange={handleUrlChange}
                    onBlur={handleUrlBlur}
                    placeholder="https://your-idp.com/sso/saml"
                    color={urlError != null ? 'ERROR' : undefined}
                    disabled={certLoading}
                />
                {urlError != null ? <span className={errorText}>{urlError}</span> : null}
                {urlError == null && urlWarning != null ? <span className={warningText}>{urlWarning}</span> : null}
            </div>

            <div className={fieldGroup}>
                <Label>Trusted Signing Certificate</Label>
                <Textarea
                    value={data.certificate}
                    onChange={handleCertChange}
                    onBlur={handleCertBlur}
                    placeholder="Paste your certificate here...&#10;-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----&#10;&#10;Or paste the base64-encoded certificate body without the header and footer lines."
                    resizable={false}
                    style={{minHeight: 160, fontFamily: 'monospace'}}
                    disabled={certLoading}
                />
                {certError != null ? <span className={errorText}>{certError}</span> : null}
                {apiError != null ? (
                    <span className={errorText}>{apiError.detailMessages?.[0]}</span>
                ) : null}
                {certLoading ? (
                    <div className={loadingIndicator}>
                        <Loading type="ICON"/>
                        <span>Validating certificate...</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};
